import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";

import { commitRuns, DEFAULT_BASIS, PRICE_RUN_KEY, type Basis, type Market, type PriceRun, type PriceRuns, type ReviewedLine, type RrpWritten } from "./api";
import { AS_OF_COPY } from "./as_of";
import { COMMIT_REVIEW_COPY } from "./commit_review";
import { CommitReviewDialog } from "./CommitReview";
import { COPY } from "./format";
import { commitRefusal, commitReviewed, commitTarget } from "./price";
import { PROJECTION_KEY } from "./projection";
import { EVAL_KEY } from "./strategies";
import { CUSTOMER_KEY } from "./customer";
import { KPI_KEY } from "./kpi";
import { LOGS_KEY } from "./logs";
import { refreshAfterCommit } from "./rrp";

/**
 * The commit bar above the table, only while a saved line exists in a market on view. Review and commit opens the review
 * (C1 v1.26), whose Commit sends the run ids of those markets (owner ruling A2). A refusal names every refused line and commits
 * nothing, in the review while it is open and on the bar once it is closed; a success closes the review and hands the committed
 * runs up so the tab shows the apply-by-hand panel. On a picked date (C1 v1.24) the bar says a commit is as of today: the date
 * moves the figures, never when a price goes live (c4-as-of-grid ruling Q3). C1 v1.38: Commit also sends the lines the review
 * lists (C2 v1.41 `expected`); a refusal re-reads the runs, so a review C2 found changed shows what is saved now, and the bar
 * stays while the review is open, so a refusal whose re-read empties the batch still shows its sentence. C1 v1.41: a line removed
 * inside the review clears a refusal the bar still showed, as the re-read now shows what is saved. C1 v1.42: the RRPs the commit
 * wrote (C2 v1.43 `rrps`, an anchor line's price) are handed up with the runs, so the apply panel names them; the Config read is
 * never stale and the Config tab is its own route, so it reads the followed RRP and its note on its next mount without a drop.
 */
export function CommitBar({ runs, markets, areaName, onCommitted, asOf = null, basis = DEFAULT_BASIS }: {
  runs: PriceRuns | undefined; markets: Market[]; areaName: string; onCommitted: (committed: PriceRun[], rrps: RrpWritten[]) => void; asOf?: string | null;
  basis?: Basis;
}) {
  const client = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const target = commitTarget(runs, markets);
  const commit = useMutation({
    mutationFn: ({ runIds, expected }: { runIds: number[]; expected: ReviewedLine[] }) => commitRuns(runIds, expected),
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: PRICE_RUN_KEY });
      // The projection compares with the committed price, which this commit just changed (C1 v1.11).
      void client.invalidateQueries({ queryKey: PROJECTION_KEY });
      void client.invalidateQueries({ queryKey: EVAL_KEY });
      // The Customer lens compares the committed prices, which this commit just changed (C1 v1.13).
      void client.invalidateQueries({ queryKey: CUSTOMER_KEY });
      // The Logs tab lists the entries this commit just wrote, each with its rule-verdict snapshot (C1 v1.14).
      void client.invalidateQueries({ queryKey: LOGS_KEY });
      // The committed lines leave the staged batch the Overview's forecast reads (C1 v1.30).
      void client.invalidateQueries({ queryKey: KPI_KEY });
      const rrps = data.rrps ?? [];
      setResult(null); setOpen(false); onCommitted(data.committed, rrps);
    },
    onError: (error) => { setResult(commitRefusal(error, runs, areaName)); void client.invalidateQueries({ queryKey: PRICE_RUN_KEY }); },
    onSettled: () => refreshAfterCommit(client),
  });
  if (target.count === 0 && !open) return null;
  return (
    <section role="region" aria-label={COPY.commitRegion} className="flex flex-wrap items-center gap-3 rounded-md border border-ks-warn bg-ks-warn/5 p-3">
      <span className="font-semibold text-ks-ink">{COPY.savedCount(target.count, target.byMarket)}</span>
      <span className="text-sm text-ks-grey-1">{COPY.commitNote}</span>
      {asOf !== null && <span className="text-sm text-ks-ink">{AS_OF_COPY.commitNote}</span>}
      <Button type="button" disabled={commit.isPending} onClick={() => { setResult(null); setOpen(true); }}>{COMMIT_REVIEW_COPY.open(target.count)}</Button>
      {result && !open && <p role="alert" className="basis-full text-sm">{result}</p>}
      <CommitReviewDialog open={open} onOpenChange={setOpen} runs={runs} markets={markets} basis={basis} count={target.count} result={result}
        pending={commit.isPending} areaName={areaName} onRemoved={() => setResult(null)}
        onConfirm={() => commit.mutate({ runIds: target.runIds, expected: commitReviewed(runs, markets) })} />
    </section>
  );
}
